import { useContext } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  approveAdService,
  rejectAdService
} from '~/modules/retailMedia/services/ad/action/review'

interface UseReviewAdActionsParams {
  adId: string
  onApproveSuccess?: () => void
  onRejectSuccess?: () => void
}

/**
 * Approve/reject mutations shared between the row-level ReviewActions
 * buttons and the expanded ReviewMediaModal, so both stay on the same
 * network calls, toasts, and cache invalidation.
 */
export const useReviewAdActions = ({
  adId,
  onApproveSuccess,
  onRejectSuccess
}: UseReviewAdActionsParams) => {
  const { tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const queryClient = useQueryClient()

  const approveBanner = useMutation({
    mutationFn: approveAdService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get-ads']
      })

      tToasts({
        prefix: 'rm:ad.messages.approve.success',
        toastFunction: toast.success
      })

      onApproveSuccess?.()
    },
    onError: () => {
      tToasts({
        prefix: 'rm:ad.messages.approve.error',
        toastFunction: toast.critical
      })
    }
  })

  const rejectBanner = useMutation({
    mutationFn: rejectAdService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get-ads']
      })

      tToasts({
        prefix: 'rm:ad.messages.reject.success',
        toastFunction: toast.success
      })

      onRejectSuccess?.()
    },
    onError: () => {
      tToasts({
        prefix: 'rm:ad.messages.reject.error',
        toastFunction: toast.critical
      })
    }
  })

  const handleApprove = () => approveBanner.mutate({ adId })

  const handleReject = (note: string) =>
    rejectBanner.mutate({ adId, body: { note } })

  return { approveBanner, rejectBanner, handleApprove, handleReject }
}
